import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createRecaptcha, type Recaptcha, type RecaptchaFactory, type RecaptchaKeys } from './recaptcha'

/** No keys yet (config still loading) or a failed config: no token, the server decides (fail open). */
const NO_RECAPTCHA: Recaptcha = {
  load: () => undefined,
  executeScore: () => Promise.resolve(null),
  renderCheckbox: () => Promise.reject(new Error('reCAPTCHA is not available')),
}

const FactoryContext = createContext<RecaptchaFactory>((keys) => createRecaptcha(keys))

/**
 * Which reCAPTCHA the app uses (Testing §3: "behind a `Recaptcha` component and hook"): the real `enterprise.js`
 * client (with `VITE_RECAPTCHA_SCRIPT_URL` for local dev and E2E), or a fake in FR tests and stories.
 */
export function RecaptchaProvider({ factory, children }: { factory: RecaptchaFactory; children: ReactNode }) {
  return <FactoryContext.Provider value={factory}>{children}</FactoryContext.Provider>
}

/** The client for the clinic's keys (`GET …/config` `recaptcha`); one per pair of keys. */
export function useRecaptcha(keys: RecaptchaKeys | undefined): Recaptcha {
  const factory = useContext(FactoryContext)
  const score = keys?.scoreSiteKey
  const checkbox = keys?.checkboxSiteKey
  return useMemo(
    () =>
      score === undefined || checkbox === undefined
        ? NO_RECAPTCHA
        : factory({ scoreSiteKey: score, checkboxSiteKey: checkbox }),
    [factory, score, checkbox],
  )
}
